import { useState } from 'react';
import { History, Clock, Trash2, X, Calculator } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { HistoryEntry } from '@/hooks/useGlobalHistory';

interface HistoryPanelProps {
  history: HistoryEntry[];
  onSelectEntry?: (entry: HistoryEntry) => void;
  onClearHistory?: () => void;
  onDeleteEntry?: (id: string) => void;
}

export function HistoryPanel({
  history,
  onSelectEntry,
  onClearHistory,
  onDeleteEntry,
}: HistoryPanelProps) {
  const [open, setOpen] = useState(false);

  const formatTimestamp = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return date.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Open calculation history"
              className="h-9 w-9 rounded-xl shrink-0 text-muted-foreground hover:text-foreground hover:bg-muted/50"
            >
              <History className="h-4 w-4" />
            </Button>
          </SheetTrigger>
        </TooltipTrigger>
        <TooltipContent>Calculation History</TooltipContent>
      </Tooltip>

      <SheetContent className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <History className="h-5 w-5 text-primary" />
            Calculation History
          </SheetTitle>
          <SheetDescription>
            View and restore your recent calculations
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 flex flex-col h-[calc(100vh-12rem)]">
          {history.length > 0 ? (
            <>
              <div className="flex justify-end mb-4">
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label="Clear all calculation history"
                  className="text-muted-foreground hover:text-destructive"
                  onClick={onClearHistory}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Clear All
                </Button>
              </div>

              <ScrollArea className="flex-1 -mx-2 px-2">
                <div className="space-y-2">
                  <AnimatePresence mode="popLayout">
                    {history.map((entry) => (
                      <motion.div
                        key={entry.id}
                        layout
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, x: -50 }}
                        className="group relative p-4 rounded-xl bg-muted/30 hover:bg-muted/50 cursor-pointer transition-colors"
                        onClick={() => {
                          onSelectEntry?.(entry);
                          setOpen(false);
                        }}
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                            <Calculator className="h-5 w-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                                {entry.type}
                              </span>
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {formatTimestamp(entry.timestamp)}
                              </span>
                            </div>
                            <p className="font-medium text-foreground truncate">
                              {entry.title}
                            </p>
                            <p className="text-sm text-muted-foreground truncate">
                              {entry.summary}
                            </p>
                          </div>

                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Delete ${entry.title} from history`}
                            className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteEntry?.(entry.id);
                            }}
                          >
                            <X className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                          </Button>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              </ScrollArea>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center flex-1 text-center">
              <History className="h-16 w-16 text-muted-foreground/30 mb-4" />
              <p className="text-muted-foreground font-medium">No history yet</p>
              <p className="text-sm text-muted-foreground/70">
                Your calculations will appear here
              </p>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
